import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { DEPT_NAMES } from '@/lib/constants';
import { toast } from 'sonner';
import { Factory, CheckCircle2, Eye, Play, Search, X } from 'lucide-react';

import {
  Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, FactBand, Fact,
  CellButton, NumberCell, TextCell,
} from '@/components/data';

import { PRIORITY_MAP } from '@/lib/status-colors';
import PhoneQueue from './Production/PhoneQueue';
import { useIsMobile } from '@/hooks/useIsMobile';

export default function MyProductionQueuePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [completeModal, setCompleteModal] = useState(null);
  const [completing, setCompleting] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');

  // Production data form
  const [productionData, setProductionData] = useState({
    quantity_completed: '',
    remarks: '',
    wastage: '',
  });

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/production/my-queue');
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load production queue');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Starting work opens a run against the batch — the same operation the floor
  // performs by scanning a process slip. The old call went to a
  // /line-items/.../batches/.../start route that has never existed.
  const handleStartProduction = async (batchId) => {
    try {
      await api.post('/production/runs/start', { batch_id: batchId });
      toast.success('Production started');
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start production');
    }
  };

  const handleCompleteProduction = async () => {
    if (!completeModal) return;
    
    const { orderId, batchId, totalQty } = completeModal;
    const qtyCompleted = parseInt(productionData.quantity_completed) || totalQty;

    if (qtyCompleted > totalQty) {
      toast.error(`Cannot complete more than ${totalQty} units`);
      return;
    }

    setCompleting(true);
    try {
      // the one canonical closer, same as the production floor uses; the old
      // /line-items/.../batches/.../complete route does not exist
      await api.post(`/production/orders/${orderId}/batches/${batchId}/complete`, {
        qty_completed: qtyCompleted,
        qty_rejected: parseInt(productionData.wastage) || 0,
        operator: user?.full_name || null,
        machine_used: completeModal.item?.machine_name || null,
        remarks: productionData.remarks || null,
      });

      if (qtyCompleted < totalQty) {
        toast.success(`${qtyCompleted} completed, ${totalQty - qtyCompleted} remaining in new batch`);
      } else {
        toast.success('Stage completed successfully');
      }
      
      setCompleteModal(null);
      setProductionData({ quantity_completed: '', remarks: '', wastage: '' });
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete production');
    }
    setCompleting(false);
  };

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(i => i.order_id?.toLowerCase().includes(q) || i.voice_number?.toLowerCase().includes(q) || i.customer_name?.toLowerCase().includes(q) || i.firm_name?.toLowerCase().includes(q));
  }, [items, searchQuery]);

  const active = filteredItems.filter(i => i.status === 'in_progress').length;
  const waiting = filteredItems.length - active;

  const startRow = (item) => handleStartProduction(item.batch_id);

  const openComplete = (item) => {
    setCompleteModal({
      orderId: item.id,
      batchId: item.batch_id,
      totalQty: item.quantity,
      item,
    });
    setProductionData({ quantity_completed: String(item.quantity ?? ''), remarks: '', wastage: '' });
  };

  const renderCompleteStrip = (item) => {
    const done = parseInt(productionData.quantity_completed) || 0;
    const short = done > 0 && done < (item.quantity || 0);
    return (
      <div className="flex flex-wrap items-end gap-2">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-brand-blue">
          Close {item.current_stage_name}
        </span>
        {[['Done', 'quantity_completed'], ['Wastage', 'wastage']].map(([label, key]) => (
          <label key={key} className="flex flex-col gap-0.5" style={{ width: '6rem' }}>
            <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
            <span className="rounded border border-slate-300 bg-white">
              <NumberCell label={label} value={productionData[key]}
                onChange={(v) => setProductionData(d => ({ ...d, [key]: v }))} />
            </span>
          </label>
        ))}
        <label className="flex flex-1 flex-col gap-0.5" style={{ minWidth: '14rem' }}>
          <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">Remarks</span>
          <span className="rounded border border-slate-300 bg-white">
            <TextCell label="Remarks" value={productionData.remarks}
              onChange={(v) => setProductionData(d => ({ ...d, remarks: v }))} />
          </span>
        </label>
        <Button size="sm" className="h-7 bg-green-600 text-white hover:bg-green-700"
          disabled={completing} onClick={handleCompleteProduction}>
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          {completing ? 'Saving…' : 'Done'}
        </Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => setCompleteModal(null)}>
          <X className="h-3.5 w-3.5" />
        </Button>
        {short && (
          <span className="w-full text-[11px] text-amber-700">
            {done} of {item.quantity} — the remaining {item.quantity - done} stays at this stage in a new batch.
          </span>
        )}
      </div>
    );
  };

  // one card per batch below lg; the same start and the same closing numbers
  if (isMobile) {
    return (
      <PhoneQueue
        items={filteredItems} loading={loading} userName={user?.full_name}
        onSearch={setSearchQuery} onRefresh={fetchQueue}
        expandedId={selectedItem}
        onToggle={(item) => setSelectedItem(item.batch_id === selectedItem ? null : item.batch_id)}
        onStart={startRow}
        onOpenOrder={(item) => navigate(`/orders/${item.id}`)}
        completing={completeModal} form={productionData}
        onFormChange={(key, value) => setProductionData(d => ({ ...d, [key]: value }))}
        onOpenComplete={openComplete} onCancelComplete={() => setCompleteModal(null)}
        onSubmitComplete={handleCompleteProduction} submitting={completing}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  const rows = [];
  for (const item of filteredItems) {
    rows.push(item);
    if (completeModal?.batchId === item.batch_id) {
      rows.push({ batch_id: `${item.batch_id}:complete`, __full: true, __render: () => renderCompleteStrip(item) });
    }
  }

  const columns = [
    {
      key: 'item',
      header: 'Job',
      width: 'minmax(13rem,1.5fr)',
      render: (i) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {i.item_name || i.product_subtype || DEPT_NAMES[i.order_type] || 'Item'}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {i.customer_name}{i.firm_name ? ` — ${i.firm_name}` : ''}
          </span>
        </>
      ),
    },
    {
      key: 'order_id',
      header: 'Order',
      width: '9.5rem',
      render: (i) => <span className="block truncate text-[11px] text-slate-400">{i.order_id}</span>,
    },
    {
      key: 'current_stage_name',
      header: 'Stage',
      width: '10rem',
      render: (i) => <span className="block truncate text-[12px] font-medium text-brand-blue">{i.current_stage_name}</span>,
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '6rem',
      render: (i) => <span className="font-medium">{i.quantity?.toLocaleString()}</span>,
      total: (list) => list.filter(r => !r.__full).reduce((s, i) => s + (i.quantity || 0), 0).toLocaleString(),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      render: (i) => (
        <span className={`text-[11px] font-medium ${
          i.priority === 'high' ? 'text-red-600' : i.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
        }`}>
          {PRIORITY_MAP[i.priority]?.label || i.priority || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      render: (i) => (
        <span className={`text-[12px] font-medium ${i.status === 'in_progress' ? 'text-blue-600' : 'text-slate-500'}`}>
          {i.status === 'in_progress' ? 'Running' : 'Waiting'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '10rem',
      align: 'right',
      render: (i) => completeModal?.batchId === i.batch_id ? null : (
        <span className="flex items-center justify-end gap-0.5">
          {i.status !== 'in_progress' ? (
            <CellButton icon={Play} label="Start" tone="accent" onClick={() => startRow(i)} />
          ) : (
            <CellButton icon={CheckCircle2} label="Complete" tone="good" onClick={() => openComplete(i)} />
          )}
          <CellButton icon={Eye} label="Open order" iconOnly
            onClick={() => navigate(`/orders/${i.id}`)} />
        </span>
      ),
    },
  ];

  const selected = filteredItems.find(i => i.batch_id === selectedItem);

  return (
    <Screen data-testid="my-production-queue-page">
      <CommandBar
        title="My Production Queue"
        subtitle={user?.full_name}
        actions={[{ key: 'refresh', label: 'Refresh', icon: Factory, onClick: fetchQueue }]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Order, customer…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: 'jobs on my machine', value: filteredItems.length, tone: 'blue' },
          { key: null, label: 'running', value: active, tone: active ? 'good' : 'default' },
          { key: null, label: 'waiting', value: waiting, tone: waiting ? 'warn' : 'default' },
        ]}
      />

      <ScreenSplit
        railWidth={selected ? '20rem' : '0rem'}
        rail={selected ? (
          <Pane
            title="Specifications"
            aside={selected.item_name || selected.current_stage_name}
            className="max-h-full"
            actions={
              <button type="button" onClick={() => setSelectedItem(null)} aria-label="Close specifications"
                className="rounded p-0.5 text-white/80 hover:bg-white/20">
                <X className="h-3.5 w-3.5" />
              </button>
            }
          >
            <div className="p-1.5">
              <FactBand min="100%" className="border-0 p-0 shadow-none">
                <Fact label="Order">{selected.order_id}</Fact>
                <Fact label="Customer">{selected.customer_name}</Fact>
                <Fact label="Stage">{selected.current_stage_name}</Fact>
                <Fact label="Qty">{selected.quantity?.toLocaleString()}</Fact>
                {Object.entries(selected.specifications || {})
                  .filter(([, v]) => v && v !== 'None' && v !== 'No')
                  .map(([k, v]) => (
                    <Fact key={k} label={k.replace(/_/g, ' ')}>{String(v)}</Fact>
                  ))}
              </FactBand>
            </div>
            {selected.special_instructions && (
              <div className="border-t border-slate-100 px-2.5 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Special instructions</p>
                <p className="mt-0.5 text-[12px] leading-snug text-amber-800">{selected.special_instructions}</p>
              </div>
            )}
          </Pane>
        ) : null}
      >
        <Pane title="My queue" aside={`${filteredItems.length} job${filteredItems.length === 1 ? '' : 's'}`}
          scroll={false} bodyClassName="flex flex-col">
          <DataGrid
            data-testid="my-queue-table"
            columns={columns}
            rows={rows}
            getRowId={(i) => i.batch_id}
            selectedId={selectedItem}
            onRowClick={(i) => !i.__full && setSelectedItem(i.batch_id === selectedItem ? null : i.batch_id)}
            showTotals={filteredItems.length > 0}
            empty={
              <span className="flex flex-col items-center gap-2 text-slate-400">
                <Factory className="h-8 w-8 text-slate-300" />
                Nothing queued for you right now.
              </span>
            }
          />
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
